import type { ApiNodeDataExternalApi, JsonSchemaObject, ProxyConfigBasic } from '@onefootprint/request-types/dashboard';
import { outputTypeFromSchema } from '@/playbook-builder/utils/output-type';
import {
  type ExternalApiFormData,
  type FormEntireBodyAssignment,
  type FormFieldExtraction,
  type HttpPropertiesFormData,
  MOCK_RESPONSE_VALUE,
} from '../../../../types';

const toKeyValueArray = (obj: Record<string, unknown> | undefined) =>
  Object.entries(obj || {}).map(([key, value]) => ({ key, value: String(value) }));

const emptyHttpProperties: HttpPropertiesFormData = {
  headers: [],
  queryParams: [],
  body: '',
  method: 'get',
  path: '',
};

type RawAssignment = { key: string; rule: unknown; outputSchema?: JsonSchemaObject; isRequired?: boolean };

const isEntireBody = (a: RawAssignment) => a.rule === 'assign_entire_output';

const parseFieldExtraction = (a: RawAssignment): FormFieldExtraction => {
  const jsonPath = (a.rule as Record<string, unknown>).jsonPath as { selector: string } | undefined;
  return {
    key: a.key,
    selector: jsonPath?.selector?.replace(/^\$\.?/, '') ?? '',
    outputType: a.outputSchema ? outputTypeFromSchema(a.outputSchema) : undefined,
    isRequired: a.isRequired ?? false,
  };
};

const parseEntireBody = (a: RawAssignment | undefined): FormEntireBodyAssignment =>
  a ? { enabled: true, key: a.key } : { enabled: false, key: '' };

export const getDefaultValues = ({
  node,
  proxyConfig,
  sandboxProxyConfig,
  isSandboxSession,
}: {
  node: { data: ApiNodeDataExternalApi };
  proxyConfig: ProxyConfigBasic | undefined;
  sandboxProxyConfig: ProxyConfigBasic | undefined;
  isSandboxSession: boolean;
}): ExternalApiFormData => {
  const config = node.data.config as Record<string, unknown>;
  const sandboxHttp = config.sandboxHttpProperties as Record<string, unknown> | undefined;

  // url is for display only (from proxyConfig or sandboxProxyConfig)
  const url = isSandboxSession ? sandboxProxyConfig?.url : proxyConfig?.url;

  const rawAssignments = (config.workflowDataAssignments as Array<RawAssignment>) || [];
  const entireBody = parseEntireBody(rawAssignments.find(isEntireBody));
  const fieldExtractions = rawAssignments.filter(a => !isEntireBody(a)).map(parseFieldExtraction);

  const source = node.data.source;
  const mockData = typeof source === 'object' && 'proxy' in source ? (source.proxy.useMockData ?? '') : '';

  return {
    config: {
      name: node.data.name || '',
      headers: toKeyValueArray(node.data.config.headers as Record<string, unknown>),
      queryParams: toKeyValueArray(node.data.config.queryParams as Record<string, unknown>),
      body: (node.data.config.body as string) || '',
      method: node.data.config.method || 'get',
      url,
      path: (node.data.config.path as string) || '',
      entireBody,
      fieldExtractions,
      requireSuccessStatusCode: (config.requireSuccessStatusCode as boolean) ?? true,
      proxyConfig: proxyConfig?.id,
      useSandboxHttpProperties: !!sandboxHttp,
      sandboxHttpProperties: sandboxHttp
        ? {
            headers: toKeyValueArray(sandboxHttp.headers as Record<string, unknown>),
            queryParams: toKeyValueArray(sandboxHttp.queryParams as Record<string, unknown>),
            body: (sandboxHttp.body as string) || '',
            method: (sandboxHttp.method as string as ExternalApiFormData['config']['method']) || 'get',
            path: (sandboxHttp.path as string) || '',
          }
        : emptyHttpProperties,
      ...(mockData
        ? { sandboxProxyConfig: MOCK_RESPONSE_VALUE, useMockData: mockData }
        : { sandboxProxyConfig: sandboxProxyConfig?.id, useMockData: '' }),
    },
  };
};
